import { Box } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useAuth } from '@verantos-dev/core-auth';
import {
  ActionMenu,
  type ActionMenuItemProps,
  type IconType,
} from '@verantos-dev/shared-components';

import type { ProjectArtifact } from '../types';
import { DeleteAnalysisDialog } from './DeleteAnalysisDialog';
import { RenameAnalysisDialog } from './RenameAnalysisDialog';

interface AnalysisRowActionsProps {
  artifact: ProjectArtifact;
}

/**
 * Per-row action menu for analysis rows on the artifacts list: creator-only
 * Rename and Delete (Delete is disabled while a run is in progress). Renders
 * nothing for non-creators (no kebab shown). Owns its dialogs via local state
 * (mirrors `AnalyticalDatasetRowActions`).
 */
export const AnalysisRowActions = ({ artifact }: AnalysisRowActionsProps) => {
  const intl = useIntl();
  const { projectId = '' } = useParams<{ projectId: string }>();
  const {
    user: { userId, auth0Id },
  } = useAuth();
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const isCreator = [userId, auth0Id].includes(artifact.owner.id);
  const isProcessing = artifact.status?.toUpperCase() === 'PROCESSING';

  if (!isCreator) return null;

  const menu: ActionMenuItemProps[] = [
    {
      id: `analysisArtifactActionMenu-rename-${artifact.id}`,
      label: intl.formatMessage({ id: 'analysisList.actionMenu.rename.label' }),
      onClick: () => setIsRenameOpen(true),
      icon: 'rename',
    },
    {
      id: `analysisArtifactActionMenu-delete-${artifact.id}`,
      label: intl.formatMessage({ id: 'analysisList.actionMenu.delete.label' }),
      disabled: isProcessing,
      onClick: () => setIsDeleteOpen(true),
      icon: 'delete',
      iconDisabled: 'deleteBlack' as IconType,
      tooltip: isProcessing
        ? intl.formatMessage({ id: 'analysisList.actionMenu.delete.inProgress' })
        : undefined,
    },
  ];

  return (
    <Box data-testid={`analysis-row-actions-${artifact.id}`} sx={{ display: 'inline-flex' }}>
      <ActionMenu
        id={`analysisArtifactActionMenu-${artifact.id}`}
        menuItems={menu}
        stopClickEventPropagation
      />
      {isRenameOpen && (
        <RenameAnalysisDialog
          projectId={projectId}
          analysisProfileId={artifact.id}
          currentName={artifact.name}
          onClose={() => setIsRenameOpen(false)}
        />
      )}
      {isDeleteOpen && (
        <DeleteAnalysisDialog
          projectId={projectId}
          analysisProfileId={artifact.id}
          name={artifact.name}
          onClose={() => setIsDeleteOpen(false)}
        />
      )}
    </Box>
  );
};
